// EDEN · reusable wayfinding builders (pure HTML strings that work with the router's data-link
// click delegation — no lifecycle needed). Breadcrumbs + a "where this sits" context bar + a
// "related items" block turn EDEN's one-level nav into layered wayfinding, so a teacher moves
// between related data (class ↔ lesson ↔ calendar ↔ week) without returning to the top menu.
// All labels are HTML-escaped here — pass raw class names / topics safely.

import { esc } from "../dom";

export interface Crumb {
  label: string; // plain text (escaped here)
  href?: string; // omit for the current (last) crumb
}
export interface Pill {
  label: string;
  href: string;
}
export interface RelatedItem {
  label: string;
  href: string;
  sub?: string;
}

/** A hierarchy breadcrumb, e.g. Home › My Classes › G4A. The last crumb is the current page. */
export function breadcrumbs(trail: Crumb[]): string {
  const parts = trail.map((c, i) => {
    const last = i === trail.length - 1;
    const el =
      c.href && !last
        ? `<a data-link href="${esc(c.href)}">${esc(c.label)}</a>`
        : `<span class="current"${last ? ' aria-current="page"' : ""}>${esc(c.label)}</span>`;
    const sep = i > 0 ? `<span class="sep" aria-hidden="true">›</span>` : "";
    return sep + el;
  });
  return `<nav class="breadcrumb" aria-label="Breadcrumb">${parts.join("")}</nav>`;
}

/** A row of quiet pills — the "where this sits" context under a lesson/plan title. */
export function contextBar(pills: Pill[]): string {
  if (!pills.length) return "";
  return `<div class="context-bar">${pills
    .map((p) => `<a class="context-pill" data-link href="${esc(p.href)}">${esc(p.label)}</a>`)
    .join("")}</div>`;
}

/** A "see also" block ending an entity page — kills dead ends (NN/g Related Content). */
export function relatedBlock(title: string, items: RelatedItem[]): string {
  if (!items.length) return "";
  const rows = items
    .map(
      (it) => `<a class="ref-row" data-link href="${esc(it.href)}">
        <span class="ref-text"><span class="label">${esc(it.label)}</span>${
          it.sub ? `<span class="sub">${esc(it.sub)}</span>` : ""
        }</span><span class="chev" aria-hidden="true">›</span></a>`,
    )
    .join("");
  return `<div class="related"><p class="related-title">${esc(title)}</p><div class="ref-list">${rows}</div></div>`;
}
